import "bootstrap/dist/css/bootstrap.min.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';
import { fetchContactInfo } from '../../redux/actions/contactAction';
import { submitContactForm } from '../../redux/actions/contactUsActions';
import { clearContactForm } from '../../redux/slices/contactUsSlice';
import { useEffect, useState } from 'react';
import { useDarkMode } from '../../App';
import { toast } from 'react-toastify';


function Contact() {
    const { t, i18n } = useTranslation('global');
    const { isDarkMode } = useDarkMode();
    const dispatch = useDispatch();
    const { data: contact, isLoading, error } = useSelector(state => state.contact);
    const { isLoading: isSubmitting, success, error: submitError, message } = useSelector(state => state.contactUs);
    
    // Form state
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        window.scrollTo(0, 0);
        dispatch(fetchContactInfo(i18n.language));
    }, [dispatch, i18n.language]);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Validate form
    const validateForm = () => {
        const errors = {};
        
        if (!formData.full_name.trim()) {
            errors.full_name = t('contact.required');
        }
        
        if (!formData.email.trim()) {
            errors.email = t('contact.required');
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = t('contact.invalidEmail');
        }
        
        if (!formData.phone.trim()) {
            errors.phone = t('contact.required');
        } else if (!/^[0-9+\-\s()]+$/.test(formData.phone)) {
            errors.phone = t('contact.invalidPhone');
        }
        
        if (!formData.subject.trim()) {
            errors.subject = t('contact.required');
        }
        
        if (!formData.message.trim()) {
            errors.message = t('contact.required');
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('full_name', formData.full_name);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('subject', formData.subject);
            formDataToSend.append('message', formData.message);
            
            await dispatch(submitContactForm(formDataToSend));
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    // Handle success/error messages
    useEffect(() => {
        if (success && message) {
            toast.success(t('contact.form_success_created'));
            setFormData({
                full_name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            });
            dispatch(clearContactForm());
        }
        
        if (submitError) {
            toast(submitError);
        }
    }, [success, message, submitError, dispatch, t]);


    return (
        <section className={`linksMark-section ${isDarkMode ? 'dark-mode' : ''}`} style={{
            backgroundColor: isDarkMode ? '#0a0e27' : undefined,
            minHeight: '100vh'
        }}>
            <div
                className={`breadcrumb-box text-center py-5 mb-4 rounded ${isDarkMode ? 'dark-mode' : ''}`}
                style={{ 
                    background: isDarkMode ? '#0a0e27' : "#000c3d0D",
                    color: isDarkMode ? 'var(--color-text)' : undefined
                }}
            >
                <h4 className="fw-bold">{t('contact.contact_us')}</h4>
                <p className="text-muted">{t('contact.home_contact_us')}</p>
            </div>

            <div className="container">
                {/* Loading State */}
                {isLoading && (
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t('contact.loading')}</span>
                        </div>
                        <p className="mt-3">{t('contact.loading')}</p>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="alert alert-danger text-center">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {t('contact.error')}
                    </div>
                )}

                {/* No Data State */}
                {!isLoading && !error && !contact && (
                    <div className="text-center my-5 text-muted">
                        <i className="fas fa-info-circle fa-3x mb-3"></i>
                        <p>{t('contact.noData')}</p>
                    </div>
                )}

                {/* Content */}
                {!isLoading && !error && contact && (
                    <>
                        <div className="row text-center mb-4">
                    {/* الكاردات الأصلية */}
                    <div className="col-md-4 mb-3">
                        <div className={`p-3 border rounded d-flex align-items-center gap-3 shadow-sm ${isDarkMode ? 'dark-mode' : ''}`} style={{ 
                            background: isDarkMode ? '#0a0e27' : "#EF7F030D",
                            borderColor: isDarkMode ? 'var(--color-border)' : undefined,
                            color: isDarkMode ? 'var(--color-text)' : undefined
                        }}>
                            {/* svg أوقات العمل */}
                            <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0.839844" y="0.910156" width="71" height="71" rx="12" fill="white" />
                                <path d="M51.1301 26.0086H26.3301C25.4412 26.0086 24.6856 26.3197 24.0634 26.9419C23.4412 27.5641 23.1301 28.3197 23.1301 29.2086V30.4086C23.1301 30.5864 23.219 30.7641 23.3967 30.9419C23.5745 31.1197 23.7523 31.2086 23.9301 31.2086C24.1079 31.2086 24.2856 31.1197 24.4634 30.9419C24.6412 30.7641 24.7301 30.5864 24.7301 30.4086V29.2086L24.8634 28.6753L33.7967 36.4086L24.8634 44.1419L24.7301 43.6086V42.4086C24.7301 42.2308 24.6412 42.053 24.4634 41.8753C24.2856 41.6975 24.1079 41.6086 23.9301 41.6086C23.7523 41.6086 23.5745 41.6975 23.3967 41.8753C23.219 42.053 23.1301 42.2308 23.1301 42.4086V43.6086C23.1301 44.4975 23.4412 45.253 24.0634 45.8753C24.6856 46.4975 25.4412 46.8086 26.3301 46.8086H51.1301C52.019 46.8086 52.7745 46.4975 53.3967 45.8753C54.019 45.253 54.3301 44.4975 54.3301 43.6086V29.2086C54.3301 28.3197 54.019 27.5641 53.3967 26.9419C52.7745 26.3197 52.019 26.0086 51.1301 26.0086ZM26.0634 27.6086C26.0634 27.6086 26.1523 27.6086 26.3301 27.6086H51.3967L39.2634 38.1419C38.9079 38.4086 38.5523 38.4086 38.1967 38.1419L26.0634 27.6086ZM51.1301 45.2086H26.3301H26.0634L34.9967 37.4753L37.1301 39.3419C37.5745 39.6975 38.1079 39.8753 38.7301 39.8753C39.3523 39.8753 39.8856 39.6975 40.3301 39.3419L42.4634 37.4753L51.3967 45.2086H51.1301ZM52.7301 43.6086C52.7301 43.6975 52.6856 43.8753 52.5967 44.1419L43.6634 36.4086L52.5967 28.6753L52.7301 29.2086V43.6086ZM20.7301 34.4086C20.7301 34.2308 20.819 34.053 20.9967 33.8753C21.1745 33.6975 21.3523 33.6086 21.5301 33.6086H26.3301C26.5079 33.6086 26.6856 33.6975 26.8634 33.8753C27.0412 34.053 27.1301 34.2308 27.1301 34.4086C27.1301 34.5864 27.0412 34.7641 26.8634 34.9419C26.6856 35.1197 26.5079 35.2086 26.3301 35.2086H21.5301C21.3523 35.2086 21.1745 35.1197 20.9967 34.9419C20.819 34.7641 20.7301 34.5864 20.7301 34.4086ZM26.3301 39.2086H19.1301C18.9523 39.2086 18.7745 39.1197 18.5967 38.9419C18.419 38.7641 18.3301 38.5864 18.3301 38.4086C18.3301 38.2308 18.419 38.053 18.5967 37.8753C18.7745 37.6975 18.9523 37.6086 19.1301 37.6086H26.3301C26.5079 37.6086 26.6856 37.6975 26.8634 37.8753C27.0412 38.053 27.1301 38.2308 27.1301 38.4086C27.1301 38.5864 27.0412 38.7641 26.8634 38.9419C26.6856 39.1197 26.5079 39.2086 26.3301 39.2086Z" fill="#EF7F03" />
                            </svg>
                            <div className="contact-info mt-3">
                                <h6 style={{ color: isDarkMode ? 'var(--color-text)' : undefined }}>{t('contact.working_hours')}</h6>
                                <p className="text-muted" style={{ color: isDarkMode ? 'var(--color-muted)' : undefined }}>{contact?.work_hours || '..............'}</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4 mb-3">
                        <div className={`p-3 border rounded d-flex align-items-center gap-3 shadow-sm ${isDarkMode ? 'dark-mode' : ''}`} style={{ 
                            background: isDarkMode ? '#0a0e27' : "#EF7F030D",
                            borderColor: isDarkMode ? 'var(--color-border)' : undefined,
                            color: isDarkMode ? 'var(--color-text)' : undefined
                        }}>
                            {/* svg إيميل */}
                            <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0.839844" y="0.910156" width="71" height="71" rx="12" fill="white" />
                                <path d="M51.1301 26.0086H26.3301C25.4412 26.0086 24.6856 26.3197 24.0634 26.9419C23.4412 27.5641 23.1301 28.3197 23.1301 29.2086V30.4086C23.1301 30.5864 23.219 30.7641 23.3967 30.9419C23.5745 31.1197 23.7523 31.2086 23.9301 31.2086C24.1079 31.2086 24.2856 31.1197 24.4634 30.9419C24.6412 30.7641 24.7301 30.5864 24.7301 30.4086V29.2086L24.8634 28.6753L33.7967 36.4086L24.8634 44.1419L24.7301 43.6086V42.4086C24.7301 42.2308 24.6412 42.053 24.4634 41.8753C24.2856 41.6975 24.1079 41.6086 23.9301 41.6086C23.7523 41.6086 23.5745 41.6975 23.3967 41.8753C23.219 42.053 23.1301 42.2308 23.1301 42.4086V43.6086C23.1301 44.4975 23.4412 45.253 24.0634 45.8753C24.6856 46.4975 25.4412 46.8086 26.3301 46.8086H51.1301C52.019 46.8086 52.7745 46.4975 53.3967 45.8753C54.019 45.253 54.3301 44.4975 54.3301 43.6086V29.2086C54.3301 28.3197 54.019 27.5641 53.3967 26.9419C52.7745 26.3197 52.019 26.0086 51.1301 26.0086ZM26.0634 27.6086C26.0634 27.6086 26.1523 27.6086 26.3301 27.6086H51.3967L39.2634 38.1419C38.9079 38.4086 38.5523 38.4086 38.1967 38.1419L26.0634 27.6086ZM51.1301 45.2086H26.3301H26.0634L34.9967 37.4753L37.1301 39.3419C37.5745 39.6975 38.1079 39.8753 38.7301 39.8753C39.3523 39.8753 39.8856 39.6975 40.3301 39.3419L42.4634 37.4753L51.3967 45.2086H51.1301ZM52.7301 43.6086C52.7301 43.6975 52.6856 43.8753 52.5967 44.1419L43.6634 36.4086L52.5967 28.6753L52.7301 29.2086V43.6086ZM20.7301 34.4086C20.7301 34.2308 20.819 34.053 20.9967 33.8753C21.1745 33.6975 21.3523 33.6086 21.5301 33.6086H26.3301C26.5079 33.6086 26.6856 33.6975 26.8634 33.8753C27.0412 34.053 27.1301 34.2308 27.1301 34.4086C27.1301 34.5864 27.0412 34.7641 26.8634 34.9419C26.6856 35.1197 26.5079 35.2086 26.3301 35.2086H21.5301C21.3523 35.2086 21.1745 35.1197 20.9967 34.9419C20.819 34.7641 20.7301 34.5864 20.7301 34.4086ZM26.3301 39.2086H19.1301C18.9523 39.2086 18.7745 39.1197 18.5967 38.9419C18.419 38.7641 18.3301 38.5864 18.3301 38.4086C18.3301 38.2308 18.419 38.053 18.5967 37.8753C18.7745 37.6975 18.9523 37.6086 19.1301 37.6086H26.3301C26.5079 37.6086 26.6856 37.6975 26.8634 37.8753C27.0412 38.053 27.1301 38.2308 27.1301 38.4086C27.1301 38.5864 27.0412 38.7641 26.8634 38.9419C26.6856 39.1197 26.5079 39.2086 26.3301 39.2086Z" fill="#EF7F03" />
                            </svg>
                            <div className="contact-info mt-3">
                                <h6 style={{ color: isDarkMode ? 'var(--color-text)' : undefined }}>{t('contact.email')}</h6>
                                <p className="text-muted" style={{ color: isDarkMode ? 'var(--color-muted)' : undefined }}>{contact?.email || '..............'}</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4 mb-3">
                        <div className={`p-3 border rounded d-flex align-items-center gap-3 shadow-sm ${isDarkMode ? 'dark-mode' : ''}`} style={{ 
                            background: isDarkMode ? '#0a0e27' : "#EF7F030D",
                            borderColor: isDarkMode ? 'var(--color-border)' : undefined,
                            color: isDarkMode ? 'var(--color-text)' : undefined
                        }}>
                            {/* svg عنوان */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 72 72" fill="none">
                                <rect width="71" height="71" rx="12" transform="matrix(-1 0 0 -1 71.5898 0.910156)" fill="white" />
                                <g clipPath="url(#clip0_100_5909)">
                                    <path d="M44.8996 50.5435C42.4996 51.2546 39.5663 51.6102 36.0996 51.6102C32.6329 51.6102 29.6996 51.2546 27.2996 50.5435C24.3663 49.7435 22.8996 48.5657 22.8996 47.0102C22.8996 45.4546 24.3663 44.2768 27.2996 43.4768C28.6329 43.1213 30.1441 42.8546 31.8329 42.6768C27.3885 35.1213 25.1663 30.0102 25.1663 27.3435C25.1663 25.3879 25.6552 23.5657 26.6329 21.8768C27.6107 20.1879 28.9441 18.8546 30.6329 17.8768C32.3218 16.899 34.1441 16.4102 36.0996 16.4102C38.0552 16.4102 39.8774 16.899 41.5663 17.8768C43.2552 18.8546 44.5885 20.1879 45.5663 21.8768C46.5441 23.5657 47.0329 25.3879 47.0329 27.3435C47.0329 30.0102 44.8107 35.1213 40.3663 42.6768C42.0552 42.8546 43.5663 43.1213 44.8996 43.4768C47.8329 44.2768 49.2996 45.4546 49.2996 47.0102C49.2996 48.5657 47.8329 49.7435 44.8996 50.5435ZM36.0996 18.8102C34.5885 18.8102 33.1663 19.1879 31.8329 19.9435C30.4996 20.699 29.4552 21.7213 28.6996 23.0102C27.9441 24.299 27.5663 25.7435 27.5663 27.3435C27.5663 28.499 28.2774 30.5879 29.6996 33.6102C31.1218 36.6324 33.0774 40.1879 35.5663 44.2768C35.6552 44.4546 35.8329 44.5435 36.0996 44.5435C36.3663 44.5435 36.5441 44.4546 36.6329 44.2768C39.1218 40.1879 41.0774 36.6324 42.4996 33.6102C43.9218 30.5879 44.6329 28.499 44.6329 27.3435C44.6329 25.7435 44.2552 24.299 43.4996 23.0102C42.7441 21.7213 41.6996 20.699 40.3663 19.9435C39.0329 19.1879 37.6107 18.8102 36.0996 18.8102ZM43.8329 45.6102C42.4996 45.2546 40.8996 45.0324 39.0329 44.9435L38.6329 45.4768C38.3663 45.9213 38.0107 46.2768 37.5663 46.5435C37.1218 46.8102 36.6329 46.9435 36.0996 46.9435C35.5663 46.9435 35.0774 46.8102 34.6329 46.5435C34.1885 46.2768 33.8329 45.9213 33.5663 45.4768L33.1663 44.9435C31.2996 45.0324 29.6996 45.2546 28.3663 45.6102C27.3885 45.8768 26.6552 46.1435 26.1663 46.4102C25.6774 46.6768 25.3441 46.899 25.1663 47.0768C25.3441 47.2546 25.6774 47.4546 26.1663 47.6768C26.6552 47.899 27.3885 48.1435 28.3663 48.4102C30.4996 48.9435 33.0774 49.2102 36.0996 49.2102C39.1218 49.2102 41.6996 48.9435 43.8329 48.4102C44.8107 48.1435 45.5441 47.899 46.0329 47.6768C46.5218 47.4546 46.8552 47.2546 47.0329 47.0768C46.8552 46.899 46.5218 46.6768 46.0329 46.4102C45.5441 46.1435 44.8107 45.8768 43.8329 45.6102ZM19.5663 44.4102C19.8329 44.3213 20.1218 44.3213 20.4329 44.4102C20.7441 44.499 20.9663 44.6768 21.0996 44.9435C21.2329 45.2102 21.2552 45.499 21.1663 45.8102C21.0774 46.1213 20.8996 46.3657 20.6329 46.5435C19.2107 47.3435 18.4996 48.099 18.4996 48.8102C18.4996 49.3435 18.8774 49.9213 19.6329 50.5435C20.3885 51.1657 21.5663 51.7435 23.1663 52.2768C26.7218 53.4324 31.0329 54.0102 36.0996 54.0102C41.1663 54.0102 45.4774 53.4324 49.0329 52.2768C50.6329 51.7435 51.8107 51.1657 52.5663 50.5435C53.3218 49.9213 53.6996 49.3435 53.6996 48.8102C53.6996 48.099 52.9885 47.3435 51.5663 46.5435C51.2996 46.3657 51.1218 46.1213 51.0329 45.8102C50.9441 45.499 50.9663 45.2102 51.0996 44.9435C51.2329 44.6768 51.4552 44.499 51.7663 44.4102C52.0774 44.3213 52.3663 44.3213 52.6329 44.4102C54.9441 45.6546 56.0996 47.1213 56.0996 48.8102C56.0996 49.7879 55.6996 50.7213 54.8996 51.6102C53.8329 52.6768 52.1441 53.6102 49.8329 54.4102C46.0107 55.7435 41.4329 56.4102 36.0996 56.4102C30.7663 56.4102 26.1885 55.7435 22.3663 54.4102C20.0552 53.6102 18.3663 52.6768 17.2996 51.6102C16.4996 50.7213 16.0996 49.7879 16.0996 48.8102C16.0996 47.1213 17.2552 45.6546 19.5663 44.4102ZM31.6996 26.5435C31.6996 25.3879 32.1218 24.3657 32.9663 23.4768C33.8107 22.5879 34.8552 22.1435 36.0996 22.1435C37.3441 22.1435 38.3885 22.5879 39.2329 23.4768C40.0774 24.3657 40.4996 25.4102 40.4996 26.6102C40.4996 27.8102 40.0774 28.8324 39.2329 29.6768C38.3885 30.5213 37.3441 30.9435 36.0996 30.9435C34.8552 30.9435 33.8107 30.5213 32.9663 29.6768C32.1218 28.8324 31.6996 27.7879 31.6996 26.5435ZM38.0996 26.5435C38.0996 26.0102 37.8996 25.5435 37.4996 25.1435C37.0996 24.7435 36.6329 24.5435 36.0996 24.5435C35.5663 24.5435 35.0996 24.7435 34.6996 25.1435C34.2996 25.5435 34.0996 26.0102 34.0996 26.5435C34.0996 27.0768 34.2996 27.5435 34.6996 27.9435C35.0996 28.3435 35.5663 28.5435 36.0996 28.5435C36.6329 28.5435 37.0996 28.3435 37.4996 27.9435C37.8996 27.5435 38.0996 27.0768 38.0996 26.5435Z" fill="#EF7F03" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_100_5909">
                                        <rect width="40.02" height="40" fill="white" transform="matrix(-1 0 0 -1 56.1094 56.4102)" />
                                    </clipPath>
                                </defs>
                            </svg>
                            <div className="contact-info mt-3">
                                <h6 style={{ color: isDarkMode ? 'var(--color-text)' : undefined }}>{t('contact.address')}</h6>
                                <p className="text-muted" style={{ color: isDarkMode ? 'var(--color-muted)' : undefined }}>{Array.isArray(contact?.address) ? contact.address.join(' / ') : '..............'}</p>
                            </div>
                        </div>
                    </div>
                    {/* كارد رقم الهاتف */}
                    <div className="col-md-4 mb-3">
                        <div className={`p-3 border rounded d-flex align-items-center gap-3 shadow-sm ${isDarkMode ? 'dark-mode' : ''}`} style={{ 
                            background: isDarkMode ? '#0a0e27' : "#EF7F030D",
                            borderColor: isDarkMode ? 'var(--color-border)' : undefined,
                            color: isDarkMode ? 'var(--color-text)' : undefined
                        }}>
                            {/* svg هاتف */}
                            <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0.5" y="0.5" width="71" height="71" rx="12" fill="white" stroke="#EF7F03" />
                                <path d="M48 44.5C46.5 44.5 45.05 44.25 43.7 43.8C43.15 43.6 42.5 43.75 42.1 44.2L39.55 46.95C35.4 44.85 31.95 41.4 29.85 37.25L32.6 34.7C33.05 34.3 33.2 33.65 33 33.1C32.55 31.75 32.3 30.3 32.3 28.8C32.3 28.1 31.7 27.5 31 27.5H25.5C24.8 27.5 24 27.8 24 28.8C24 41.1 34.9 52 47.2 52C48.2 52 48.5 51.2 48.5 50.5V45C48.5 44.3 47.9 44.5 48 44.5Z" fill="#EF7F03"/>
                            </svg>
                            <div className="contact-info mt-3">
                                <h6 style={{ color: isDarkMode ? 'var(--color-text)' : undefined }}>{t('contact.phone_number')}</h6>
                                <p className="text-muted" style={{ color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                                    {(() => {
                                        if (!contact?.phones) return '..............';
                                        
                                        // إذا كان array
                                        if (Array.isArray(contact.phones)) {
                                            return contact.phones.join(' / ');
                                        }
                                        
                                        // إذا كان object
                                        if (typeof contact.phones === 'object') {
                                            return Object.values(contact.phones).filter(phone => phone).join(' / ');
                                        }
                                        
                                        // إذا كان string
                                        return contact.phones;
                                    })()}
                                </p>
                            </div>
                        </div>
                    </div>
               
                </div>

                <div className="row mb-4">
                    <div className="col-12">
                        {contact?.map_link && (
                            <iframe
                                src={contact.map_link}
                                width="100%"
                                height="400"
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                style={{ border: 0 }}
                            ></iframe>
                        )}
                    </div>
                </div>

                {/* نموذج التواصل */}
              

                {/* عرض البيانات الإضافية من stay-in-touch */}
                {contact && (
                    <div className="row justify-content-center mb-5">
                        <div className="col-md-8">
                            <div className={`shadow p-5 rounded ${isDarkMode ? 'dark-mode' : 'bg-white'}`} style={{ 
                                marginTop: "-150px",
                                backgroundColor: isDarkMode ? '#0a0e27' : undefined,
                                borderColor: isDarkMode ? 'var(--color-border)' : undefined,
                                color: isDarkMode ? 'var(--color-text)' : undefined
                            }}>
                                <h4 className="fw-bold text-center mb-4" style={{ color: isDarkMode ? 'var(--color-text)' : undefined }}>{t('contact.additionalInfo')}</h4>
                                
                                {/* WhatsApp Link */}
                                {contact.whatsapp_link && (
                                    <div className="mb-3 text-center">
                                        <a 
                                            href={contact.whatsapp_link} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="btn btn-success px-4 py-2"
                                            style={{ borderRadius: "15px" }}
                                        >
                                            <i className="fab fa-whatsapp me-2"></i>
                                            {t('contact.whatsappContact')}
                                        </a>
                                    </div>
                                )}

                                {/* Web Links */}
                                {contact.web_link && Array.isArray(contact.web_link) && contact.web_link.length > 0 && (
                                    <div className="mb-3 text-center">
                                        <h6 style={{ color: isDarkMode ? 'var(--color-text)' : undefined }}>{t('contact.websiteLinks')}</h6>
                                        {contact.web_link.map((link, index) => (
                                            <div key={index} className="mb-2">
                                                <a 
                                                    href={link} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="btn btn-outline-primary px-3 py-2"
                                                    style={{ borderRadius: "10px" }}
                                                >
                                                    <i className="fas fa-globe me-2"></i>
                                                    {link}
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Map */}
                                {contact.map_link && (
                                    <div className="text-center">
                                        <h6 className="mb-3" style={{ color: isDarkMode ? 'var(--color-text)' : undefined }}>{t('contact.ourLocation')}</h6>
                                        <iframe
                                            src={contact.map_link}
                                            width="100%"
                                            height="300"
                                            allowFullScreen=""
                                            loading="lazy"
                                            referrerPolicy="no-referrer-when-downgrade"
                                            style={{ border: 0, borderRadius: "10px" }}
                                        ></iframe>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}



                  <div className="row justify-content-center mb-5">
                    <div className="col-lg-8 col-md-10">
                        <div className={`contact-form-container shadow-lg p-4 rounded-3 ${isDarkMode ? 'dark-mode' : 'bg-white'}`} style={{ 
                            backgroundColor: isDarkMode ? '#0a0e27' : undefined,
                            borderColor: isDarkMode ? 'var(--color-border)' : undefined,
                            color: isDarkMode ? 'var(--color-text)' : undefined,
                            border: isDarkMode ? '1px solid var(--color-border)' : '1px solid #e9ecef'
                        }}>
                            <div className="text-center mb-4">
                                <h3 className="fw-bold mb-2" style={{ color: isDarkMode ? 'var(--color-text)' : undefined }}>
                                    {t('contact.submit_custom_request')}
                                </h3>
                                <p className="text-muted" style={{ color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                                    {t('contact.form_description', 'نحن هنا لمساعدتك، أرسل لنا رسالتك وسنرد عليك في أقرب وقت')}
                                </p>
                            </div>
                            
                            <form onSubmit={handleSubmit}>
                                <div className="row g-3 ">
                                    <div className="col-md-6  py-2 my-2">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className={`form-control ${formErrors.full_name ? 'is-invalid' : ''}`}
                                                id="full_name"
                                                name="full_name"
                                                value={formData.full_name}
                                                onChange={handleInputChange}
                                                placeholder={t('contact.full_name')}
                                                style={{
                                                    backgroundColor: isDarkMode ? '#0a0e27' : undefined,
                                                    borderColor: isDarkMode ? 'var(--color-border)' : undefined,
                                                    color: isDarkMode ? 'var(--color-text)' : undefined
                                                }}
                                            />
                                            <label htmlFor="full_name" style={{ color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                                                {t('contact.full_name')} <span className="text-danger">*</span>
                                            </label>
                                            {formErrors.full_name && (
                                                <div className="invalid-feedback">{formErrors.full_name}</div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-6  py-2 my-2">
                                        <div className="form-floating">
                                            <input
                                                type="email"
                                                className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder={t('contact.email_address')}
                                                style={{
                                                    backgroundColor: isDarkMode ? '#0a0e27' : undefined,
                                                    borderColor: isDarkMode ? 'var(--color-border)' : undefined,
                                                    color: isDarkMode ? 'var(--color-text)' : undefined
                                                }}
                                            />
                                            <label htmlFor="email" style={{ color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                                                {t('contact.email_address')} <span className="text-danger">*</span>
                                            </label>
                                            {formErrors.email && (
                                                <div className="invalid-feedback">{formErrors.email}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="row g-3">
                                    <div className="col-md-6  py-2 my-2">
                                        <div className="form-floating">
                                            <input
                                                type="tel"
                                                className={`form-control ${formErrors.phone ? 'is-invalid' : ''}`}
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                placeholder={t('contact.phone_number')}
                                                style={{
                                                    backgroundColor: isDarkMode ? '#0a0e27' : undefined,
                                                    borderColor: isDarkMode ? 'var(--color-border)' : undefined,
                                                    color: isDarkMode ? 'var(--color-text)' : undefined
                                                }}
                                            />
                                            <label htmlFor="phone" style={{ color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                                                {t('contact.phone_number')} <span className="text-danger">*</span>
                                            </label>
                                            {formErrors.phone && (
                                                <div className="invalid-feedback">{formErrors.phone}</div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-6  py-2 my-2">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className={`form-control ${formErrors.subject ? 'is-invalid' : ''}`}
                                                id="subject"
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleInputChange}
                                                placeholder={t('contact.subject')}
                                                style={{
                                                    backgroundColor: isDarkMode ? '#0a0e27' : undefined,
                                                    borderColor: isDarkMode ? 'var(--color-border)' : undefined,
                                                    color: isDarkMode ? 'var(--color-text)' : undefined
                                                }}
                                            />
                                            <label htmlFor="subject" style={{ color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                                                {t('contact.subject')} <span className="text-danger">*</span>
                                            </label>
                                            {formErrors.subject && (
                                                <div className="invalid-feedback">{formErrors.subject}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="mb-4">
                                    <div className="form-floating">
                                        <textarea
                                            className={`form-control ${formErrors.message ? 'is-invalid' : ''}`}
                                            id="message"
                                            name="message"
                                            rows="5"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            placeholder={t('contact.enter_your_message')}
                                            style={{
                                                backgroundColor: isDarkMode ? '#0a0e27' : undefined,
                                                borderColor: isDarkMode ? 'var(--color-border)' : undefined,
                                                color: isDarkMode ? 'var(--color-text)' : undefined,
                                                minHeight: '120px'
                                            }}
                                        ></textarea>
                                        <label htmlFor="message" style={{ color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                                            {t('contact.enter_your_message')} <span className="text-danger">*</span>
                                        </label>
                                        {formErrors.message && (
                                            <div className="invalid-feedback">{formErrors.message}</div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="text-center mt-4">
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-lg px-5 py-3 rounded-pill fw-bold"
                                        disabled={isSubmitting}
                                        style={{
                                            backgroundColor: isDarkMode ? 'var(--color-primary)' : '#007bff',
                                            borderColor: isDarkMode ? 'var(--color-primary)' : '#007bff',
                                            minWidth: '200px',
                                            boxShadow: isDarkMode ? '0 4px 15px rgba(0, 123, 255, 0.3)' : '0 4px 15px rgba(0, 123, 255, 0.2)',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = isDarkMode ? '0 6px 20px rgba(0, 123, 255, 0.4)' : '0 6px 20px rgba(0, 123, 255, 0.3)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = isDarkMode ? '0 4px 15px rgba(0, 123, 255, 0.3)' : '0 4px 15px rgba(0, 123, 255, 0.2)';
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                {t('contact.sending')}
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-paper-plane me-2"></i>
                                                {t('contact.submit_request')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
                    </>
                )}
            </div>
            
            {/* Toast Container */}

            
            <style>
                {`
                    /* Form Floating Labels */
                    .form-floating > .form-control:focus ~ label,
                    .form-floating > .form-control:not(:placeholder-shown) ~ label {
                        color: ${isDarkMode ? 'var(--color-primary)' : '#007bff'};
                        transform: scale(0.85) translateY(-0.5rem) translateX(0.15rem);
                    }
                    
                    .form-floating > .form-control:focus {
                        border-color: ${isDarkMode ? 'var(--color-primary)' : '#007bff'};
                        box-shadow: 0 0 0 0.2rem ${isDarkMode ? 'rgba(0, 123, 255, 0.25)' : 'rgba(0, 123, 255, 0.25)'};
                    }
                    
                    /* Dark Mode Form Controls */
                    .dark-mode .form-control {
                        background-color: #0a0e27 !important;
                        border-color: var(--color-border) !important;
                        color: var(--color-text) !important;
                    }
                    
                    .dark-mode .form-control:focus {
                        background-color: #0a0e27 !important;
                        border-color: var(--color-primary) !important;
                        color: var(--color-text) !important;
                        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
                    }
                    
                    .dark-mode .form-floating > label {
                        color: var(--color-muted) !important;
                    }
                    
                    /* Button Hover Effects */
                    .btn-primary:hover {
                        transform: translateY(-2px) !important;
                        box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4) !important;
                    }
                    
                    /* Form Container */
                    .contact-form-container {
                        background: linear-gradient(135deg, ${isDarkMode ? '#0a0e27 0%, #1a1f3a 100%' : '#ffffff 0%, #f8f9fa 100%'});
                        border-radius: 20px;
                        box-shadow: ${isDarkMode ? '0 10px 30px rgba(0, 0, 0, 0.3)' : '0 10px 30px rgba(0, 0, 0, 0.1)'};
                    }
                    
                    /* Responsive Design */
                    @media (max-width: 768px) {
                        .contact-form-container {
                            margin: 0 15px;
                        }
                        
                        .btn-lg {
                            padding: 12px 24px !important;
                            font-size: 16px !important;
                        }
                    }
                `}
            </style>
        </section>
    );
}

export default Contact;